/** @jsxImportSource @emotion/react */
import React, { memo, useCallback } from 'react';
import { css } from '@emotion/react';
import { Dialog, Portal } from '@ark-ui/react';
import { useTranslation } from '@vitvio/locales';
import { useTheme } from '../provider/useTheme';
import { Text } from './text';
import type { ModalSizeToken, ButtonTypeToken } from '../themes/types';
import type { IconName } from '../icons';
import { zIndex } from '../constants/zIndex';
import { focusRing, overlay } from '../constants/style';
import { CoreButton } from './CoreButton';
import { CoreIcon } from './CoreIcon';

/**
 * Footer action configuration for CoreModal.
 */
export interface CoreModalButton {
  /** Translation key shown on the button label. */
  label: string;
  /** Optional action callback; modal still closes after click. */
  onClick?: () => void;
  /** Optional leading icon token. */
  iconLeft?: IconName;
  /** Optional trailing icon token. */
  iconRight?: IconName;
  /** Button style variant (defaults to `secondary`). */
  type?: ButtonTypeToken;
}

/**
 * Props for the CoreModal component.
 */
export interface CoreModalProps {
  /** Controlled open state. */
  isOpen: boolean;
  /** Called when the dialog requests to close (escape, backdrop, close button). */
  onClose: () => void;
  /** Modal width preset. */
  size?: ModalSizeToken;
  /** Translation key for the main heading. */
  headerPath: string;
  /** Optional translation key for supporting copy below the heading. */
  titlePath?: string;
  /** Optional body content block. */
  children?: React.ReactNode;
  /** Footer buttons used only when `renderFooter` is not provided. */
  buttons?: CoreModalButton[];
  /** Whether the close button is rendered in the top-right corner. */
  closeButton?: boolean;
  /** Custom footer renderer; when set, `buttons` is ignored. */
  renderFooter?: () => React.ReactNode;
  /** Optional interpolation values for translated text fields. */
  values?: Record<string, string | number>;
  /** Optional custom background color for the overlay. */
  overlayBg?: string;
  /** Optional CSS backdrop-filter for the overlay (e.g. blur). */
  backdropFilter?: string;
}

/**
 * Modal dialog with semantic title/body/footer structure and optional action buttons.
 */
export const CoreModal: React.FC<CoreModalProps> = memo(
  ({
    isOpen,
    onClose,
    size = 'md',
    headerPath,
    titlePath,
    children,
    buttons,
    closeButton = true,
    renderFooter,
    values,
    overlayBg,
    backdropFilter,
  }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { getColor, getSpacing, getRadius, getLayout } = theme;

    const modalWidth = getLayout('modalWidth', size);
    const paddingVertical = getSpacing('4');
    const paddingHorizontal = getSpacing('5');
    const borderRadiusValue = getRadius('md');

    const handleButtonClick = useCallback(
      (button: CoreModalButton) => {
        if (button.onClick) {
          button.onClick();
        }
        onClose();
      },
      [onClose],
    );

    return (
      <Dialog.Root
        open={isOpen}
        lazyMount
        unmountOnExit
        onOpenChange={({ open }) => {
          if (!open) {
            onClose();
          }
        }}
      >
        <Portal>
          <Dialog.Backdrop
            css={css`
              position: fixed;
              inset: 0;
              background-color: ${overlayBg ?? getColor('fg.constant')};
              opacity: ${overlay.opacity};
              z-index: ${zIndex.modalBackdrop};
              ${backdropFilter ? `backdrop-filter: ${backdropFilter};` : ''}
            `}
          />
          <Dialog.Positioner
            css={css`
              position: fixed;
              inset: 0;
              z-index: ${zIndex.modal};
              display: flex;
              align-items: center;
              justify-content: center;
              padding: ${getSpacing('4')};
            `}
          >
            <Dialog.Content
              css={css`
                position: relative;
                background-color: ${getColor('bg')};
                max-width: ${modalWidth};
                width: 100%;
                border-radius: ${borderRadiusValue};
              `}
            >
              {/* Close Button - positioned absolutely */}
              {closeButton && (
                <div
                  css={css`
                    position: absolute;
                    top: ${getSpacing('2')};
                    right: ${getSpacing('2')};
                    z-index: 1;
                  `}
                >
                  {/* TODO: compose with CoreButton iconOnly once Dialog.CloseTrigger supports asChild properly */}
                  <Dialog.CloseTrigger
                    type="button"
                    aria-label={t('common:button.closeModal')}
                    css={css`
                      display: inline-flex;
                      align-items: center;
                      justify-content: center;
                      width: ${theme.sizes.layout.inputHeight}px;
                      height: ${theme.sizes.layout.inputHeight}px;
                      border: none;
                      border-radius: ${getRadius('sm')};
                      background: ${getColor('button.bg.ghost')};
                      cursor: pointer;

                      &:hover {
                        background: ${getColor('button.bg.ghost.hover')};
                      }

                      &:active {
                        background: ${getColor('button.bg.ghost.active')};
                      }

                      &:focus-visible {
                        outline: ${focusRing.width} solid ${getColor('border.accent')};
                        outline-offset: ${focusRing.offset};
                      }
                    `}
                  >
                    <CoreIcon icon="close" size="sm" color="fg.muted" />
                  </Dialog.CloseTrigger>
                </div>
              )}

              {/* Header Section */}
              <div
                css={css`
                  padding: ${paddingVertical} ${paddingHorizontal};
                  padding-right: ${getSpacing('12')}; /* Extra space for close button */
                `}
              >
                <Dialog.Title
                  css={css`
                    margin: 0;
                    padding: 0;
                  `}
                >
                  {/* TODO(vitvio-ui): replace Chakra-backed Text with CoreText primitive when available. */}
                  <Text path={headerPath} font="headings.S_SemiBold" colorToken="fg" value={values} />
                </Dialog.Title>
              </div>

              {/* Description Section */}
              {titlePath && (
                <Dialog.Description asChild>
                  <div
                    css={css`
                      padding: ${paddingVertical} ${paddingHorizontal};
                    `}
                  >
                    <Text path={titlePath} font="M_Regular" colorToken="fg" value={values} />
                  </div>
                </Dialog.Description>
              )}

              {/* Content Section */}
              {children && (
                <div
                  css={css`
                    padding: ${paddingVertical} ${paddingHorizontal};
                  `}
                >
                  {children}
                </div>
              )}

              {/* Footer Section */}
              {(buttons || renderFooter) && (
                <div
                  css={css`
                    padding: ${paddingVertical} ${paddingHorizontal};
                    display: flex;
                    gap: ${getSpacing('3')};
                    justify-content: flex-start;
                  `}
                >
                  {renderFooter
                    ? renderFooter()
                    : buttons?.map((button, index) => (
                        <CoreButton
                          key={index}
                          size="md"
                          label={button.label}
                          iconLeft={button.iconLeft}
                          iconRight={button.iconRight}
                          type={button.type || 'secondary'}
                          onClick={() => handleButtonClick(button)}
                        />
                      ))}
                </div>
              )}
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    );
  },
);

CoreModal.displayName = 'CoreModal';

export default CoreModal;
